/**
 * vibecheck kickoff — Initialize VibeCheck in a project
 */

import * as fs from 'fs';
import * as path from 'path';
import { execSync } from 'child_process';
import type { Command } from 'commander';
import type { GlobalOptions } from '../types';
import { loadConfig, writeConfig, ensureVibecheckDir } from '../core/config';
import { detectProject } from '../core/project-detect';
import * as out from '../ui/output';
import { colors, icons } from '../ui/theme';

export function registerKickoff(program: Command): void {
  const cmd = program
    .command('kickoff')
    .description('Initialize VibeCheck in your project')
    .option('--template <name>', 'Use a preset template (nextjs, react, node, python, monorepo)')
    .option('--force', 'Overwrite existing config')
    .option('--dry-run', 'Show what would be created without creating it')
    .option('--connect', 'Connect to GitHub after initialization');

  cmd
    .command('init')
    .description('Full interactive initialization')
    .action(async (opts: Record<string, unknown>, command: Command) => {
      const globals = command.parent!.parent!.opts() as GlobalOptions;
      const parentOpts = command.parent!.opts();
      await runKickoff({ ...globals, ...parentOpts, mode: 'init' });
    });

  cmd
    .command('quick')
    .description('Auto-detect everything, zero prompts')
    .action(async (opts: Record<string, unknown>, command: Command) => {
      const globals = command.parent!.parent!.opts() as GlobalOptions;
      const parentOpts = command.parent!.opts();
      await runKickoff({ ...globals, ...parentOpts, mode: 'quick' });
    });

  cmd
    .command('status')
    .description('Show current project configuration')
    .action(async (opts: Record<string, unknown>, command: Command) => {
      const globals = command.parent!.parent!.opts() as GlobalOptions;
      await runStatus(globals);
    });

  cmd
    .command('connect')
    .description('Connect project to GitHub — sets up CI workflow, webhooks, and badge')
    .option('--repo <owner/repo>', 'GitHub repository (auto-detected from git remote)')
    .option('--workflow', 'Install GitHub Actions workflow', true)
    .option('--badge', 'Add VibeCheck badge to README', true)
    .option('--no-workflow', 'Skip GitHub Actions workflow')
    .option('--no-badge', 'Skip README badge')
    .action(async (connectOpts: Record<string, unknown>, command: Command) => {
      const globals = command.parent!.parent!.opts() as GlobalOptions;
      await runConnect({ ...globals, ...connectOpts });
    });

  // Default action (no subcommand)
  cmd.action(async (opts: Record<string, unknown>, command: Command) => {
    const globals = command.parent!.opts() as GlobalOptions;
    const allOpts = { ...globals, ...opts };
    await runKickoff({ ...allOpts, mode: 'quick' });
    // If --connect flag was passed, also run connect
    if ((allOpts as any).connect) {
      await runConnect(allOpts as any);
    }
  });
}

interface KickoffOpts extends GlobalOptions {
  mode: 'init' | 'quick';
  template?: string;
  force?: boolean;
  dryRun?: boolean;
  connect?: boolean;
}

interface ConnectOpts extends GlobalOptions {
  repo?: string;
  workflow?: boolean;
  badge?: boolean;
}

async function runKickoff(opts: KickoffOpts): Promise<void> {
  const start = performance.now();
  const rootPath = opts.path || process.cwd();

  out.brandHeader('kickoff', 'Initialize VibeCheck in your project');
  out.nl();

  // Check for existing config
  const existingConfig = path.join(rootPath, '.vibecheckrc.json');
  if (fs.existsSync(existingConfig) && !opts.force) {
    out.warn('VibeCheck is already initialized in this project.');
    out.info(`Run ${colors.info('vibecheck kickoff --force')} to reinitialize.`);
    out.info(`Run ${colors.info('vibecheck kickoff status')} to see current config.`);
    return;
  }

  // Detect project
  out.info('Detecting project structure...');
  const project = detectProject(rootPath);

  if (opts.json) {
    out.jsonOutput({ project, configPath: existingConfig });
    return;
  }

  // Build config
  const config = loadConfig(rootPath);
  config.project.name = project.name;
  config.project.framework = project.framework;
  config.project.language = project.language;
  config.project.packageManager = project.packageManager;

  if (opts.dryRun) {
    out.nl();
    out.info('Dry run — would create:');
    console.log(`  ${colors.dim(icons.file)} .vibecheckrc.json`);
    console.log(`  ${colors.dim(icons.folder)} .vibecheck/`);
    out.nl();
    console.log(colors.dim(JSON.stringify(config, null, 2)));
    return;
  }

  // Write config
  const configPath = writeConfig(rootPath, config);
  ensureVibecheckDir(rootPath);

  // Print summary
  out.nl();
  console.log(out.box(`${icons.rocket} VibeCheck initialized successfully`, [
    `${colors.dim('Project:')}    ${colors.bold(project.name)}`,
    `${colors.dim('Framework:')}  ${project.framework}`,
    `${colors.dim('Language:')}   ${project.language}`,
    `${colors.dim('Pkg Mgr:')}   ${project.packageManager}`,
    `${colors.dim('Config:')}    .vibecheckrc.json`,
  ]));

  out.nl();
  console.log(colors.bold('Next steps:'));
  console.log(`  1. Run ${colors.info('vibecheck scan')} to analyze your codebase`);
  console.log(`  2. Run ${colors.info('vibecheck doctor')} to check your setup`);
  console.log(`  3. Run ${colors.info('vibecheck --help')} to see all commands`);
  out.nl();
  out.success(`${out.completedIn(performance.now() - start)}`);
}

// ═══════════════════════════════════════════════════════════════════════════
// CONNECT — GitHub Integration
// ═══════════════════════════════════════════════════════════════════════════

async function runConnect(opts: ConnectOpts): Promise<void> {
  const rootPath = opts.path || process.cwd();

  out.brandHeader('Connect', 'GitHub Integration');
  out.nl();

  // 1. Detect GitHub repo
  let repoSlug = opts.repo;
  if (!repoSlug) {
    try {
      const remoteUrl = execSync('git remote get-url origin', { cwd: rootPath, encoding: 'utf-8', timeout: 5000 }).trim();
      // Parse: git@github.com:owner/repo.git or https://github.com/owner/repo.git
      const sshMatch = remoteUrl.match(/github\.com[:/]([^/]+\/[^/.]+)/);
      if (sshMatch) repoSlug = sshMatch[1].replace(/\.git$/, '');
    } catch { /* not a git repo or no remote */ }
  }

  if (!repoSlug) {
    out.errorWithFix(
      'Could not detect GitHub repository',
      'No git remote found or --repo not specified.',
      'vibecheck kickoff connect --repo owner/repo'
    );
    process.exitCode = 3;
    return;
  }

  out.info(`Repository: ${colors.bold(repoSlug)}`);
  out.nl();

  const created: string[] = [];

  // 2. Install GitHub Actions workflow
  if (opts.workflow !== false) {
    const workflowDir = path.join(rootPath, '.github', 'workflows');
    const workflowPath = path.join(workflowDir, 'vibecheck.yml');

    if (fs.existsSync(workflowPath)) {
      out.info('GitHub Actions workflow already exists — skipping.');
    } else {
      if (!fs.existsSync(workflowDir)) fs.mkdirSync(workflowDir, { recursive: true });

      const workflow = `name: VibeCheck

on:
  push:
    branches: [main, master]
  pull_request:
    branches: [main, master]

jobs:
  vibecheck:
    name: VibeCheck Scan
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4

      - uses: actions/setup-node@v4
        with:
          node-version: '20'

      - name: Install dependencies
        run: npm ci

      - name: Run VibeCheck scan
        run: npx vibecheck-ai scan full --ci --format json --output vibecheck-report.json

      - name: Run VibeCheck ship check
        run: npx vibecheck-ai ship check --json

      - name: Upload report
        if: always()
        uses: actions/upload-artifact@v4
        with:
          name: vibecheck-report
          path: vibecheck-report.json
`;

      fs.writeFileSync(workflowPath, workflow, 'utf-8');
      created.push('.github/workflows/vibecheck.yml');
      out.success('Created GitHub Actions workflow');
    }
  }

  // 3. Add badge to README
  if (opts.badge !== false) {
    const readmePath = path.join(rootPath, 'README.md');
    if (fs.existsSync(readmePath)) {
      let readme = fs.readFileSync(readmePath, 'utf-8');
      const badgeMarkdown = `[![VibeCheck](https://img.shields.io/badge/VibeCheck-Protected-22c55e?style=flat-square)](https://github.com/${repoSlug}/actions)`;

      if (readme.includes('VibeCheck') && readme.includes('badge')) {
        out.info('README already has a VibeCheck badge — skipping.');
      } else {
        // Insert badge after first heading or at top
        const headingMatch = readme.match(/^(#\s+.+\n)/);
        if (headingMatch) {
          readme = readme.replace(headingMatch[0], `${headingMatch[0]}\n${badgeMarkdown}\n`);
        } else {
          readme = `${badgeMarkdown}\n\n${readme}`;
        }
        fs.writeFileSync(readmePath, readme, 'utf-8');
        created.push('README.md (badge added)');
        out.success('Added VibeCheck badge to README');
      }
    } else {
      out.info('No README.md found — skipping badge.');
    }
  }

  // 4. Save GitHub config to .vibecheckrc.json
  const configPath = path.join(rootPath, '.vibecheckrc.json');
  if (fs.existsSync(configPath)) {
    try {
      const config = JSON.parse(fs.readFileSync(configPath, 'utf-8'));
      config.github = { repo: repoSlug, connected: true, connectedAt: new Date().toISOString() };
      fs.writeFileSync(configPath, JSON.stringify(config, null, 2) + '\n', 'utf-8');
    } catch { /* ignore */ }
  }

  // 5. Create .gitignore entries
  const gitignorePath = path.join(rootPath, '.gitignore');
  if (fs.existsSync(gitignorePath)) {
    let gitignore = fs.readFileSync(gitignorePath, 'utf-8');
    const entries = ['.vibecheck/screenshots/', '.vibecheck/scan-results.json'];
    const toAdd = entries.filter(e => !gitignore.includes(e));
    if (toAdd.length > 0) {
      gitignore += `\n# VibeCheck\n${toAdd.join('\n')}\n`;
      fs.writeFileSync(gitignorePath, gitignore, 'utf-8');
      created.push('.gitignore (vibecheck entries)');
    }
  }

  out.nl();

  if (opts.json) {
    out.jsonOutput({ repo: repoSlug, created, connected: true });
    return;
  }

  // Summary
  if (created.length > 0) {
    console.log(out.box(`${icons.globe} GitHub Connected`, [
      `${colors.dim('Repository:')}  ${colors.bold(repoSlug)}`,
      '',
      ...created.map(f => `${colors.success(icons.success)} ${f}`),
    ]));
  } else {
    out.info('Already connected — nothing to do.');
  }

  out.nl();
  console.log(colors.bold('Next steps:'));
  console.log(`  1. ${colors.info('git add . && git commit -m "Add VibeCheck CI"')}`);
  console.log(`  2. ${colors.info('git push')} — VibeCheck will run on every PR`);
  console.log(`  3. ${colors.info('vibecheck scan full')} — run a local scan first`);
  out.nl();
}

async function runStatus(opts: GlobalOptions): Promise<void> {
  const rootPath = opts.path || process.cwd();

  out.brandHeader('kickoff status', 'Current project configuration');
  out.nl();

  const configPath = path.join(rootPath, '.vibecheckrc.json');
  if (!fs.existsSync(configPath)) {
    out.warn('No VibeCheck config found in this project.');
    out.info(`Run ${colors.info('vibecheck kickoff')} to initialize.`);
    return;
  }

  const config = loadConfig(rootPath);
  const project = detectProject(rootPath);

  if (opts.json) {
    out.jsonOutput({ config, project });
    return;
  }

  console.log(out.box(`${icons.gear} Project Configuration`, [
    `${colors.dim('Name:')}        ${config.project.name}`,
    `${colors.dim('Framework:')}   ${config.project.framework}`,
    `${colors.dim('Language:')}    ${config.project.language}`,
    `${colors.dim('Pkg Mgr:')}    ${config.project.packageManager}`,
    '',
    `${colors.dim('Scan:')}        ${config.scan.categories.join(', ')}`,
    `${colors.dim('Severity:')}    ${config.scan.severity}+`,
    `${colors.dim('Firewall:')}    ${config.firewall.enabled ? 'enabled' : 'disabled'}`,
  ]));
  out.nl();
}
